import React from 'react';
import { compose } from 'lodash/fp';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';
import { genId } from '../../utils';
import {
  // Form as BaseForm,
  FormNotAuth as NotAuthForm,
  Button as BaseButton,
  Select as BaseSelect,
} from '../FormItems';
import { SelectOptions } from '../FormItems/SelectOptions';
import { withTranslation, withFieldError } from '../../hoc';
// import { useTranslation } from 'react-i18next';
// import { Link } from 'react-router-dom';

const StyledBaseForm = styled(NotAuthForm)`
  &&& {
    max-width: 420px;
    width: 100%;
    margin: 0 auto;
    height: auto;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 18px 40px rgba(76, 129, 190, 0.16);
    border: 1px solid rgba(138, 164, 210, 0.18);

    @media only screen and (max-width: 480px) {
      max-width: 100%;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
      border-radius: 10px;
    }

    div.card-body {
      padding: 24px 20px;
      display: flex;
      flex-direction: column;
      gap: 18px;

      @media only screen and (max-width: 480px) {
        padding: 16px 14px;
      }

      > button {
        margin-left: 0;
        margin-right: 0;
        margin-top: 6px;
      }

      > p {
        margin-left: 0;
        margin-right: 0;
        align-self: flex-end;
        font-size: 0.875rem;
        color: #6b7a97;

        a {
          color: #3a6bb0;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.2s ease;

          &:hover {
            color: #274f88;
          }
        }
      }
    }
  }
`;

export const Button = styled(BaseButton)`
  &&& {
    background: linear-gradient(135deg, #4c81be 0%, #325fa5 100%);
    border: none;
    text-transform: capitalize;
    width: 100%;
    cursor: pointer;
    height: 2.7rem;
    color: #ffffff;
    -webkit-tap-highlight-color: transparent;
    box-shadow: 0 12px 24px rgba(76, 129, 190, 0.35);
    border-radius: 10px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 16px 30px rgba(76, 129, 190, 0.38);
      background: linear-gradient(135deg, #3b6fb0 0%, #264f8d 100%);
    }
  }
`;

export const Form = withTranslation((props) => {
  const {
    renderActionButtons: renderActionButtonsProp = renderActionButtons,
    renderTitle: renderTitleProp = renderTitle,
    ...rest
  } = props;
  return (
    <StyledBaseForm
      {...rest}
      renderActionButtons={renderActionButtonsProp}
      renderTitle={renderTitleProp}
    />
  );
});

const renderTitle = ({ tr, title }) => {
  return (
    <div className="text-center">
      <h3 className="mb-3 mt-6 font-weight-bold">
        <strong>{tr(title)}</strong>
      </h3>
    </div>
  );
};

const renderActionButtons = ({ name, isLoading }) => {
  return <Button type="submit" name={name} isLoading={isLoading} />;
};

const StyledIcon = styled(Icon)`
  &&& {
    font-size: 19px;
    color: #2196f3 !important;
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
  }
`;

const BaseFieldWrapper = styled.div`
  background: #f5f8ff;
  border-radius: 14px;
  border: 1px solid rgba(200, 214, 238, 0.9);
  position: relative;
  padding: 14px 18px 12px 26px;
  transition: border 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  &&& {
    margin-bottom: 1rem;
  }

  &:focus-within {
    border-color: #4c81be;
    box-shadow: 0 14px 28px rgba(76, 129, 190, 0.18);
    background: #ffffff;
  }
`;

const BaseField = ({ icon = '', children = null, render = () => null, ...rest }) => {
  const props = { icon, children, render, ...rest };
  const { className } = props;
  return (
    <BaseFieldWrapper className={`md-form BaseFieldWrapper ${className}`}>
      <StyledIcon className="prefix blue-text" name={icon} />
      {children || render(props)}
    </BaseFieldWrapper>
  );
};

const InputComponent = compose(
  withTranslation,
  withFieldError,
)((props) => {
  const {
    tr,
    icon,
    name = '',
    type = 'text',
    value = '',
    onChange,
    onBlur,
    required = false,
    disabled = false,
    placeholder = '',
    localeOptions,
    options,
    className,
    ...rest
  } = props;
  const listid = genId();
  const hasOptions = localeOptions || options;
  return (
    <BaseField icon={icon}>
      <input
        type={type}
        id={name}
        value={value}
        name={name}
        onChange={onChange}
        onBlur={onBlur}
        className="form-control"
        required={required}
        disabled={disabled}
        list={listid}
      />
      {hasOptions && (
        <datalist id={listid}>
          <SelectOptions localeOptions={localeOptions} options={options} {...rest} />
        </datalist>
      )}
      <label htmlFor={name}>{tr(placeholder)}</label>
    </BaseField>
  );
});

export const Input = styled(InputComponent)`
  div.DisplayError {
    margin-left: 40px;
  }
  &&& input {
    border: none;
    border-bottom: ${({ fieldValidationProps, name }) => {
      const { errors } = fieldValidationProps ? fieldValidationProps[name] : {};
      return errors && errors.length ? '1px solid red' : '1px solid #bdbdbd';
    }};
    padding: 6px 0 4px 0;
    font-size: 15px;
    color: #1f2e4d;
    background: transparent;
  }
  &&& input:focus {
    outline: none;
    border-bottom: 2px solid #4c81be;
    box-shadow: none;
  }
`;

const SelectWrapper = styled(BaseField)`
  &&& select {
    width: 84%;
  }
`;

export const Select = withFieldError((props) => {
  const { icon, label: _, ...rest } = props;
  return (
    <SelectWrapper icon={icon}>
      <BaseSelect {...rest} />
    </SelectWrapper>
  );
});

const BigCheckboxWrapper = styled.div`
  && {
    padding-left: 0;
    label.form-check-label {
      margin-left: 13px;
      font-size: 14px;
    }
  }
`;

export const BigCheckbox = withTranslation((props) => {
  const {
    className = '',
    name = '',
    onChange = () => {},
    value = false,
    label = '',
    disabled = false,
    tr,
    children,
  } = props;
  // const { t } = useTranslation();
  return (
    <BigCheckboxWrapper className={`form-check flex position-rel ${className}`}>
      <input
        className="form-check-input m-l-0 check-box"
        name={name}
        onChange={() =>
          onChange({
            target: { name, value: !value },
          })
        }
        type="checkbox"
        checked={value}
        id={name}
        disabled={disabled}
      />
      <label htmlFor={name} className="check-box"></label>
      <label className="form-check-label" htmlFor={name}>
        {children}
        {/* {tr(label)} */}
      </label>
    </BigCheckboxWrapper>
  );
});
